import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { formatOccupantsShort } from 'lib/offer/occupancyUtils'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { capitalise } from 'lib/string/stringUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

import PropertyIndividualRoomSelectRoom from './PropertyIndividualRoomSelectRoom'

interface Props {
  occupancyRoom: OfferPageRoom
  /** Room-scoped options (capacity / guest limits already applied). */
  selectableRoomOptions: Array<RoomRateViews>
  currentPropertyRoomTypeId: string
  saleUnit?: string
  rooms: Array<OfferPageRoom>
  duration: number
  checkInDate?: string
}

function PropertyIndividualRoomTypeSelectionModal(props: Props) {
  const {
    saleUnit,
    occupancyRoom,
    rooms,
    selectableRoomOptions,
    currentPropertyRoomTypeId,
    duration,
    checkInDate,
  } = props

  const intl = useIntl()
  const modalElement = useModalElementContext()
  const dispatch = useOfferPageDispatch()

  // Read age thresholds from the current room option instead of the offer. They are per-room but
  // uniform across a property in practice; fall back to any option if the current one is absent.
  const currentRoomOption = selectableRoomOptions.find(option => option.propertyRoom.id === currentPropertyRoomTypeId)
  const occupancyFormatOptions = (currentRoomOption ?? selectableRoomOptions[0])?.propertyRoom.ageCategoryThresholds

  const roomIndex = rooms.findIndex(r => r.id === occupancyRoom.id)
  const roomNumber = roomIndex >= 0 ? roomIndex + 1 : 1

  useEffect(() => {
    if (roomIndex === -1) {
      modalElement.resolve()
    }
  }, [modalElement, roomIndex])

  const close = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const snackbarActionLabel = intl.formatMessage({
    id: '9zynkmMImt4w2_jv9nfx',
    defaultMessage: 'OK',
    description: 'Snackbar action button label after updating a room package selection',
  })

  const noDatesSelected = !checkInDate

  const roomOptionsForSelection = useMemo(() => {
    return selectableRoomOptions
      .map((roomOption) => {
        if (!noDatesSelected && roomOption.rates.length === 0) {
          return undefined
        }
        const cheapestDisplayPrice = noDatesSelected ?
          0 :
            roomOption.rates.reduce((cheapest, rate) => {
              return rate.totals.displayPrice < cheapest ? rate.totals.displayPrice : cheapest
            }, Number.MAX_SAFE_INTEGER)
        return {
          roomOption: { ...roomOption, rates: noDatesSelected ? [] : roomOption.rates },
          cheapestDisplayPrice,
          isCurrentSelection: roomOption.roomId === currentPropertyRoomTypeId,
        }
      })
      .filter((roomOptionSelection): roomOptionSelection is NonNullable<typeof roomOptionSelection> => !!roomOptionSelection)
      .sort((left, right) => {
        if (left.isCurrentSelection && !right.isCurrentSelection) {
          return -1
        }
        if (!left.isCurrentSelection && right.isCurrentSelection) {
          return 1
        }
        return left.cheapestDisplayPrice - right.cheapestDisplayPrice
      })
  }, [currentPropertyRoomTypeId, noDatesSelected, selectableRoomOptions])

  const onSelectOption = useCallback((roomTypeId: string, optionId: string | undefined) => {
    if (!roomTypeId || (!noDatesSelected && !optionId)) {
      return
    }
    // With no dates there are no rates to pick, so a room-type switch must be rate-less; the
    // incoming optionId is the prior room's rate key (passed through as currentOptionId) and would
    // otherwise be carried onto the new room type, which matches no rate for it.
    const resolvedOptionId = noDatesSelected ? undefined : optionId
    const nextRooms = rooms.map(room => room.id === occupancyRoom.id ?
        { ...room, roomId: roomTypeId, rateId: resolvedOptionId } :
      room,
    )
    dispatch({ type: OfferPageActions.setRooms, rooms: nextRooms })
    showSnackbar(
      intl.formatMessage(
        {
          id: 'CQRz2ZPk6Ur0lik63kBS',
          defaultMessage: '{saleUnit} {roomNumber} has been updated',
          description: 'Snackbar message confirming room package selection was updated',
        },
        { saleUnit: capitalise(saleUnit ?? ''), roomNumber },
      ),
      'success',
      { action: { label: snackbarActionLabel } },
    )
    close()
  }, [close, dispatch, intl, noDatesSelected, saleUnit, occupancyRoom, roomNumber, rooms, snackbarActionLabel])

  const isDesktopLayout = useScreenSizeUp('tablet')
  const optionVariant = isDesktopLayout ? 'stacked' : 'carousel'

  if (!saleUnit || roomIndex === -1) {
    return null
  }

  return (
    <ModalBase height="full" size="L">
      <ModalHeader
        title={intl.formatMessage(
          {
            id: 'A839zcPS7VrHbkTBL37m',
            defaultMessage: '{saleUnit} {roomNumber} of {totalRooms}',
            description: 'Room selection modal title showing current room number',
          },
          {
            saleUnit: capitalise(saleUnit),
            roomNumber,
            totalRooms: rooms.length,
          },
        )}
        subtitle={formatOccupantsShort(occupancyRoom, occupancyFormatOptions)}
        onCloseButtonClick={close}
      />
      <ModalBody>
        <Pane background="neutral-six" tabletBackground="none" padding="8 0 0 0" tabletPadding={12} desktopPadding={20}>
          <Group direction="vertical" gap={8} tabletGap={12}>
            {roomOptionsForSelection.length === 0 && (
              <MessageBanner kind="warning" density="compact">
                {intl.formatMessage({
                  id: 'eZABPHfBhIi3EDkk3p3e',
                  defaultMessage: 'No room options available right now.',
                  description: 'Fallback message in property individual room type selection modal when no options are available',
                })}
              </MessageBanner>
            )}
            {roomOptionsForSelection.map((roomOptionSelection) => <PropertyIndividualRoomSelectRoom
              key={roomOptionSelection.roomOption.roomId}
              optionVariant={optionVariant}
              roomOption={roomOptionSelection.roomOption}
              currentRoomTypeId={currentPropertyRoomTypeId}
              currentOptionId={occupancyRoom.rateId}
              occupancyRoom={occupancyRoom}
              duration={duration}
              noRateSelectionEnabled={noDatesSelected}
              onSelectRate={onSelectOption}
            />)}
          </Group>
        </Pane>
      </ModalBody>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state) => {
  return {
    saleUnit: 'room',
    rooms: state.rooms,
    duration: state.duration,
    checkInDate: state.checkInDate,
  }
}, PropertyIndividualRoomTypeSelectionModal)
